<template lang="pug">
.field-container.full-width.padding-top(v-if="displaySmartControl")
    label {{ $t(`${baseTranslationPath}.rule`) }}
    aiq-select(v-model="selectedRule"
                  :max-length="250"
                  :placeholder="$t(`${baseTranslationPath}.select_rule_placeholder`)"
                  value-key="id"
                  @change = "itemChanged"
                  :clearable="true")
        aiq-option(v-for="item in rules",
                        :key="item.id",
                        :label="item.name",
                        :value="item")
.field-container.full-width.padding-top(v-if="itemSelected")
    label {{ $t(`${baseTranslationPath}.version`) }}
    aiq-input(v-model="selectedRule.version"
                :max-length="250"
                )
.field-container.full-width.padding-top(v-if="itemSelected")
    label {{ $t(`${baseTranslationPath}.doc_owner`) }}
    aiq-input(v-model="selectedRule.payload.owner"
                :placeholder="$t(`${baseTranslationPath}.doc_owner_placeholder`)"
                :max-length="250"
                )
.stages-title(v-if="itemSelected") {{ $t(`${baseTranslationPath}.stages`) }}
.field-group(v-if="itemSelected")
    .group-item(v-for="(stage, index) in selectedRule.payload.stages" :key="stage.name")
        .field-container
            span.stage-name {{ stage.name }}
        .field-container.full-width.padding-top.is-required
            label {{ $t(`${baseTranslationPath}.select_agent_team`, {min: stage.min_assignee_count}) }}
            aiq-select(v-model="stage.assignees"
                        :max-length="250"
                        value-key="uniqueKey"
                        multiple
                        :reserve-keyword="false")
                aiq-option(v-for="item in agentsAndTeams", :key="item.uniqueKey", :label="item.name", :value="item")
    .field-container.full-width.padding-top
      label {{ $t(`${baseTranslationPath}.notify`) }}
        aiq-tooltip(effect="dark"
              :show-after="1000"
              :content="$t(`${baseTranslationPath}.email_tooltip`)")
              fa-icon.question-mark(:icon="['fas', 'circle-question']")
      InputTag(@input="enterEmail"
              @update-tag="selectedRule.payload.external_emails = $event"
              :tagValue="selectedRule.payload.external_emails"
              :placeholder="$t(`${baseTranslationPath}.enter_email`)"
              )

    .field-container.full-width.padding-top
      label {{ $t(`${baseTranslationPath}.expiry`) }}
      aiq-input(v-model="selectedRule.payload.frequency_cycle"
                :max-length="250"
                min="1"
                type="number")

</template>
<script>
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { AGENT_LIMIT } from '@/constants/pagination';
import InputTag from '@/components/InputTag.vue';
import { validateEmail } from '@/libs/validationUtils';
import { checkIfRuleSatisfied } from '../common/util';
const DEFAULT_FREQUENCY_CYCLE = 365;
export default {
  name: 'smart-control-drive',
  components: {
    InputTag,
  },
  data() {
    return {
      agentsAndTeams: [],
      isRuleSatisfied: false,
      itemSelected: false,
      defaultVersion: '1.0',
      displaySmartControl: false,
      isSmartControlDocument: false,
      currentRule: null,
      selectedRule: null,
      baseTranslationPath: 'settings_tab.smart_drive_tab.smart_control',
    };
  },
  emits: ['rule-satisfied'],
  watch: {
    selectedRule: {
      /**
 * Handler for selectedRule watcher.
 * Emits 'rule-satisfied' event with information about whether the selected rule is satisfied.
 * If the selected rule is null, it means the rule is satisfied and the event will be emitted with 'isRuleSatisfied' set to true.
 * If the selected rule is not null, it checks whether all stages in the rule have enough assignees and emits the event with 'isRuleSatisfied' set to the result of the check.
 */
      handler(newValue) {
        if (!newValue) {
          this.$emit('rule-satisfied', {
            isRuleSatisfied: true,
            expirySatisfied: true,
            selectedRule: null,
            versionSatisfied: true,
          });
        } else {
          const stages = get(newValue, 'payload.stages', []);
          const isRuleSatisfied = checkIfRuleSatisfied(stages);
          const version = get(newValue, 'version', '');
          const versionSatisfied = !isEmpty(version);
          const expiry = get(newValue, 'payload.frequency_cycle');

          const expirySatisfied = expiry !== null && expiry !== undefined;

          this.$emit('rule-satisfied', {
            isRuleSatisfied,
            versionSatisfied,
            expirySatisfied,
            selectedRule: newValue,
          });
        }
      },
      deep: true,
    },
  },
  props: {
    sortedTeams: {
      type: Array,
      default: () => [],
    },
    documentRecord: {
      type: Object,
      default: () => {},
    },
    isNew: {
      type: Boolean,
      default: false,
    },
    rules: {
      type: Array,
      default: () => [],
    },
  },
  async mounted() {
    await this.init();
  },

  methods: {
  /**
   * Initializes the component by fetching the list of agents and teams and storing them in 'agentsAndTeams'.
   */
    async init() {
      this.isSmartControlDocument = !!get(this.documentRecord, 'meta.smart_control', false);

      if (this.isNew || !this.isSmartControlDocument) {
        if (this.rules.length === 0) {
          this.displaySmartControl = false;
          return;
        }
        this.displaySmartControl = true;
      }

      const agents = await this.$store.dispatch('agents/getAgentsList',
        [{limit: AGENT_LIMIT}, true],
      );
      const agent_data = get(agents, 'data.models', []);

      const agentSelectable = agent_data.map((agent) => {
        return {
          id: agent.id,
          name: agent.fullName,
          type: 'agent',
        };
      });
      const teamSelectable = this.sortedTeams.map((team) => {
        return {
          id: team.id,
          name: team.name,
          type: 'team',
        };
      });
      this.agentsAndTeams = [...agentSelectable, ...teamSelectable];

      this.appendUiqueKeytoAgentsAndTeams();
    },
    appendUiqueKeytoAgentsAndTeams() {
      this.agentsAndTeams = this.agentsAndTeams.map((agent) => {
        return {
          ...agent,
          uniqueKey: `${agent.type}-${agent.id}`,
        };
      });
    },
    attachDefaultAssignees() {
      const stages = get(this.selectedRule, 'payload.stages', []);
      const updatedStages = stages.map((stage) => {
        const default_assignees = get(stage, 'default_assignees', []);
        const mappedDefaultAssignees = default_assignees.map((assignee) => {
          return {
            ...assignee,
            uniqueKey: `${assignee.type}-${assignee.id}`,
          };
        });

        return {
          ...stage,
          assignees: mappedDefaultAssignees,
          uniqueKey: `${stage.type}-${stage.id}`,
        };
      });

      this.selectedRule.payload.stages = updatedStages;
    },

    itemChanged(item) {
      if (!item) {
        this.itemSelected = false;
        this.selectedRule = null;
      } else {
        this.itemSelected = true;
        this.selectedRule = { ...item };
        this.selectedRule.version = this.selectedRule.version || this.defaultVersion;
        const existingParticipants = get(this.selectedRule, 'payload.external_emails', []);
        this.selectedRule.payload.external_emails = Array.isArray(existingParticipants) ? [...existingParticipants] : [];
        this.selectedRule.payload.frequency_cycle = DEFAULT_FREQUENCY_CYCLE;
        this.attachDefaultAssignees();
      }
    },
    enterEmail(email) {
      const normalizedEmail = String(email).trim().toLowerCase();

      if (!validateEmail(normalizedEmail)) {
        this.$aiq.notify.error(this.$t(`${this.baseTranslationPath}.invalid_email`));
        return;
      }

      const external_emails = get(this.selectedRule, 'payload.external_emails', []);
      const isDuplicate = external_emails.some(participant => String(participant).trim().toLowerCase() === normalizedEmail);
      if (isDuplicate) {
        return;
      }

      this.selectedRule.payload.external_emails.push(email);
    },
  },
};
</script>
<style lang="scss" scoped>
.field-container {
  margin-top: 10px;
}
.stage-name {
  font-weight: bold;
  font-size: 14px;
}
.smart-control-drive {
  margin-bottom: 20px;
  margin-top: 20px;
}
.stages-title {
  margin-top: 20px;
  font-weight: bold;
  font-size: 14px;
}
.padding-top {
  padding-top: 10px;
}
.btn {
  display: flex;
  justify-content: flex-end;
}

.question-mark {
  cursor: pointer;
  padding-left: 10px;
}

.group-item {
  margin-left: 50px;
  padding-top: 10px;
}
</style>
